import type React from "react";
import { useTranslation } from "react-i18next";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useModal } from "hooks/useModal";

import { ModalRenderType } from "components/Modal/Modal.model";

import { TeacherModals } from "constants/modals-constants";

import { Divider } from "components/Divider/Divider";
import { EncircledIcon } from "components/EncircledIcon";
import { Modal } from "components/Modal";
import { Note } from "components/Note/Note";
import { StatusTag } from "components/StatusTag/StatusTag";

import { Heading2, Heading4 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssFlex } from "styles/helpers/layout";

import { StyledInviteOptionBox, StyledProList } from "./SelectInviteMethodModal.styled";

export const SelectInviteMethodModal: React.FC = () => {
  const { t } = useTranslation("teacher-dashboard");
  const { getStudentTermByDomain } = useDomainHandler();
  const { isModalOpen, closeModal, openModal } = useModal();

  const isCurrentlyOpen = isModalOpen(TeacherModals.SelectInviteMethod);

  return (
    <Modal
      closeOnClickOutside
      dataCy={TeacherModals.SelectInviteMethod}
      isOpen={isCurrentlyOpen}
      renderToElementType={ModalRenderType.REACT_PORTAL}
      width="50rem"
      onClose={closeModal}
    >
      <CssFlex flexDirection="column" gap={1.5} padding="2rem 0">
        <Heading2 padding="0 2rem">
          {t("teacher-dashboard:invitationManagement.selectInviteMethodModal.title", {
            studentTerm: getStudentTermByDomain({ usePlural: true }),
          })}
        </Heading2>
        <Divider />
        <CssFlex flexDirection="column" gap={1.5} padding="0 2rem">
          <ParagraphLarge>
            {t("teacher-dashboard:invitationManagement.selectInviteMethodModal.description", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </ParagraphLarge>
          {/* -- Invite by email -- */}
          <StyledInviteOptionBox
            data-cy="create-email-invite-button"
            role="button"
            onClick={() => openModal(TeacherModals.CreateEmailInviteBundle)}
          >
            <EncircledIcon iconName="fas fa-envelope" variant="default" />
            <CssFlex flexDirection="column" gap={1}>
              <CssFlex alignItems="center" gap={1}>
                <Heading4>
                  {t(
                    "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByEmail.title",
                  )}
                </Heading4>
                <StatusTag
                  variant="success"
                  label={t(
                    "teacher-dashboard:invitationManagement.selectInviteMethodModal.tags.recommended",
                  )}
                />
              </CssFlex>
              <ParagraphLarge>
                {t(
                  "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByEmail.description",
                  {
                    studentTerm: getStudentTermByDomain(),
                    studentTermPlural: getStudentTermByDomain({ usePlural: true }),
                  },
                )}
              </ParagraphLarge>
              <StyledProList>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByEmail.advantages.mostSecure",
                  )}
                </li>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByEmail.advantages.individualTracking",
                  )}
                </li>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByEmail.advantages.automaticExpiration",
                  )}
                </li>
              </StyledProList>
            </CssFlex>
          </StyledInviteOptionBox>
          {/* -- Invite by link -- */}
          <StyledInviteOptionBox
            data-cy="create-link-invite-button"
            role="button"
            onClick={() => openModal(TeacherModals.CreateInviteLink)}
          >
            <EncircledIcon iconName="fas fa-link" variant="default" />
            <CssFlex flexDirection="column" gap={1}>
              <Heading4>
                {t(
                  "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByLink.title",
                )}
              </Heading4>
              <ParagraphLarge>
                {t(
                  "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByLink.description",
                  {
                    studentTerm: getStudentTermByDomain({ usePlural: true }),
                  },
                )}
              </ParagraphLarge>
              <Note variant="warning">
                {t(
                  "teacher-dashboard:invitationManagement.selectInviteMethodModal.options.inviteByLink.securityWarning",
                )}
              </Note>
            </CssFlex>
          </StyledInviteOptionBox>
        </CssFlex>
      </CssFlex>
    </Modal>
  );
};
